/**
 * §7.4 MIPS Exposure — 2026 logic (D13 year module; spec v14 §7.4, worked
 * examples E/F/G pinned in the frozen 2026 suite).
 *
 * What a participant's MIPS final score looks like, derived from the APP:
 *   reported:   final = 0.5·Q + 0.3·PI + 20 + CPB      (IA auto-100 → +20)
 *   reweighted: final = 0.75·Q + 25 + CPB              (PI→0: Quality 75 / IA 25,
 *                                                       the APP's own uniform rule)
 *   PI zero:    final = 0.5·Q + 0 + 20 + CPB           (must-report hard fail —
 *                                                       max 70, the locked penalty)
 * Q = the RAW §3 composite (mipsQualityInput — QPS floors never reach MIPS).
 * Adjustment: 75 neutral; linear slide to −9% at ≤18.75; >75 positive but
 * budget-neutrality-scaled (magnitude unknowable prospectively → flagged, not
 * computed). PY2026 performance pays in CY2028.
 *
 * APP-owned on purpose: NO MIPS component code is reused or modified here
 * (owner decision 2026-07-15; the MIPS-side dedup is backlog R10).
 */
import type { AppProgramConstants } from '@/lib/app-plus-data-service';

export interface ExposureInputs {
  /** The RAW quality composite (AppQualityScore.mipsQualityInput). */
  qualityPct: number;
  /** Entity PI submission score 0-100; null/undefined = no PI submission planned. */
  piPct?: number | null;
  /** Complex patient bonus 0-10 (optional labeled estimate; defaults to constants.cpbDefault). */
  cpb?: number;
}

export interface ExposureBranch {
  finalScore: number;            // capped at 100
  /** Negative/zero adjustment percent (0 at the threshold, −9 floor). NULL above the threshold. */
  adjustmentPct: number | null;
  /** True above the threshold — positive but budget-neutrality-scaled (not computable prospectively). */
  positiveScaled: boolean;
}

export interface MipsExposureResult {
  /** Needs a PI score; null when no PI submission is planned. */
  reported: ExposureBranch | null;
  reweighted: ExposureBranch;
  /** The red-flag case: must-report clinician hard-failing PI (max final 70). */
  piZero: ExposureBranch;
  breakEvens: {
    /** Min quality to clear the threshold when reporting with a perfect PI. */
    reportedQualityMinAtPerfectPi: number;
    /** Min quality to clear the threshold on the reweighted branch. */
    reweightedQualityMin: number;
    /** Report-vs-reweight crossover: report ONLY if projected PI exceeds this. */
    piCrossover: number;
  };
  /** e.g. "PY2026 → CY2028". */
  paymentYearLabel: string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** 75 → 0; linear to −9% at 18.75; ≤18.75 → the full −9%. Above 75 → null (positive, scaled). */
export function adjustmentPctForScore(finalScore: number, constants: AppProgramConstants): number | null {
  const t = constants.mipsThreshold;
  const floorAt = constants.mipsFullNegativeAtOrBelow;
  const maxNeg = constants.mipsMaxNegativeAdjustmentPct; // e.g. -9
  if (finalScore > t) return null;
  if (finalScore <= floorAt) return maxNeg;
  return round2(maxNeg * (t - finalScore) / (t - floorAt));
}

function branch(finalRaw: number, constants: AppProgramConstants): ExposureBranch {
  const finalScore = round2(Math.min(100, finalRaw));
  const adjustmentPct = adjustmentPctForScore(finalScore, constants);
  return { finalScore, adjustmentPct, positiveScaled: finalScore > constants.mipsThreshold };
}

export function computeMipsExposure(inputs: ExposureInputs, constants: AppProgramConstants): MipsExposureResult {
  const q = inputs.qualityPct;
  const cpb = inputs.cpb ?? constants.cpbDefault;
  const w = constants.appWeightsPct;                 // { quality: 50, pi: 30, ia: 20, cost: 0 }
  const wq = w.quality / 100, wpi = w.pi / 100, wia = w.ia / 100;
  const iaAuto = constants.iaAutoCreditPct;          // 100
  const rq = constants.reweightedQualityPct / 100;   // 0.75
  const ria = constants.reweightedIaPct;             // 25 (flat points: 0.25 × 100)
  const t = constants.mipsThreshold;

  const reported = inputs.piPct != null
    ? branch(wq * q + wpi * inputs.piPct + wia * iaAuto + cpb, constants)
    : null;
  const reweighted = branch(rq * q + ria + cpb, constants);
  const piZero = branch(wq * q + wia * iaAuto + cpb, constants);

  return {
    reported,
    reweighted,
    piZero,
    breakEvens: {
      reportedQualityMinAtPerfectPi: round2((t - wpi * 100 - wia * iaAuto - cpb) / wq),
      reweightedQualityMin: round2((t - ria - cpb) / rq),
      // Report only if projected PI > (wq·Q + wia·100 subsumed) … spec form: (0.25Q + 5) ÷ 0.3
      piCrossover: round2(((rq - wq) * q + ria - wia * iaAuto) / wpi),
    },
    paymentYearLabel: `PY${constants.programYear} → CY${constants.programYear + constants.paymentYearOffset}`,
  };
}
